import {
  useState,
  useCallback,
  useMemo,
  useRef,
  memo,
  useEffect,
  type MutableRefObject,
} from "react";
import type { FeatureCollection, Geometry } from "geojson";
// @ts-ignore - Type definitions might be missing but the package works at runtime
import ReactMapGL, {
  Source,
  Layer,
  MapRef,
  type MapMouseEvent,
} from "react-map-gl/mapbox";
// @ts-ignore - side-effect CSS import path is not typed in this package setup
import "mapbox-gl/dist/mapbox-gl.css";
import styled from "@emotion/styled";
import { surface, spacing, fontSize, dataVizLayout } from "@atlas-int/tokens";

import { useWorldGeoJSON } from "./loadWorldGeoJSON";
import {
  UIView,
  computeTotalSumByTradeFlow,
  worldGroupDatum,
  getDataTradeDirection,
} from "@atlas-int/data";
import {
  TradeDirection,
  TradeFlow,
  type TradeDataItem,
} from "@atlas-int/data/types";
import transformLocations from "./transformLocations";
import {
  primaryCountryColor,
  primaryCountryStrokeColor,
  getReferenceLocation,
  SMALL_COUNTRY_AREA_THRESHOLD,
  TOOLTIP_HEIGHT_ESTIMATE,
  getPinnedTooltipAnchorCoords,
  getBoundingBoxArea,
} from "./Utils";
import ZoomControls from "../growth/ZoomControls";
import { useDownload } from "@atlas-int/viz";
import type {
  GeoMapTradeDatum,
  CountryFeature,
  CountryProperties,
  LocationMetadataResult,
} from "./types";

// MAPBOX TOKEN - consider moving to environment variable
const MAPBOX_TOKEN =
  "pk.eyJ1IjoiaGFydmFyZGdyb3d0aGxhYiIsImEiOiJja2JpMWpoN2wwYXc3MnRudm02ZnZwbWJnIn0.QnDwcoWQV-zC8CfXJz_lMw";

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const SelectedLocationContainer = styled.div`
  position: absolute;
  bottom: 25px;
  right: 0px;
  padding: 0px ${spacing[2]};
  margin-right: ${spacing[2]};
  font-size: ${fontSize.base};
  background-color: rgba(255, 255, 255, 0.5);
  &::before {
    display: inline-block;
    margin-right: ${spacing[2]};
    width: ${spacing[4]};
    height: ${spacing[4]};
    border: 1px solid ${surface.white};
    background-color: ${primaryCountryColor};
    content: "";
  }
`;

interface ChartProps {
  inputData: GeoMapTradeDatum[];
  queryVariables: {
    exporter: string;
    importer: string;
    productId: string;
    year: number;
    view: UIView;
  };
  handleTooltip: (feature: CountryFeature | undefined) => void;
  /** Called instead of handleTooltip when auto-pinning a small country tooltip at fixed screen coords.
   *  Pass `undefined` as feature to hide the pinned tooltip (e.g. when country scrolls out of view). */
  handlePinnedTooltip?: (
    feature: CountryFeature | undefined,
    coords?: [number, number],
  ) => void;
  tradeDirection: TradeDirection;
  tradeFlow: TradeFlow;
  isLoading?: boolean;
  locationMetadataState: LocationMetadataResult;
  /** Ref set to `true` by the parent when the user explicitly closes the pinned tooltip.
   *  Prevents repinTooltip from re-showing the pin on subsequent pan/zoom.
   *  The parent is responsible for resetting it to `false` when the selected country changes. */
  pinnedTooltipDismissedRef?: MutableRefObject<boolean>;
}

const Chart = ({
  inputData,
  queryVariables,
  handleTooltip,
  handlePinnedTooltip,
  tradeDirection,
  tradeFlow,
  locationMetadataState,
  pinnedTooltipDismissedRef,
}: ChartProps) => {
  const { exporter, importer, year, view } = queryVariables;
  const { canvasRef, dataRef } = useDownload();
  const mapRef = useRef<MapRef>(null);

  const [hoveredPolygonId, setHoveredPolygonId] = useState<string>("");
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const worldGeojson = useWorldGeoJSON();
  const autoPinnedFeatureRef = useRef<CountryFeature | undefined>(undefined);
  /** Geographic [lng, lat] of the northernmost bbox edge centre — stable across viewport changes. */
  const autoPinnedGeoCentroidRef = useRef<[number, number] | undefined>(
    undefined,
  );

  const clearAutoPinnedTooltip = useCallback(() => {
    autoPinnedFeatureRef.current = undefined;
    autoPinnedGeoCentroidRef.current = undefined;
    handlePinnedTooltip?.(undefined);
  }, [handlePinnedTooltip]);

  const referenceLocation = useMemo(
    () => getReferenceLocation({ exporter, importer }),
    [exporter, importer],
  );

  const locationMetadata =
    locationMetadataState.status === "success"
      ? locationMetadataState.data
      : undefined;
  const locationLoading = locationMetadataState.status === "loading";
  const locationError =
    locationMetadataState.status === "error"
      ? locationMetadataState.error
      : undefined;

  // Total value is now computed and provided by the parent (index.tsx)
  // via the VisualizationOutputContext. This chart no longer needs to
  // compute or push the total value up.
  //
  // For World→World Markets queries the API returns exportValue/importValue with
  // inverted semantics. transformLocations corrects this in the GeoJSON feature
  // properties (so colors and tooltip values match the original user-facing
  // tradeDirection), but the raw inputData is still inverted.  Use the
  // data-adjusted direction when summing raw inputData.
  const isWorldToWorldQuery = useMemo(
    () =>
      view === UIView.Markets &&
      exporter === worldGroupDatum.groupId &&
      importer === worldGroupDatum.groupId,
    [view, exporter, importer],
  );
  const dataTradeDirection = useMemo(
    () => getDataTradeDirection({ tradeDirection, exporter, importer, view }),
    [tradeDirection, exporter, importer, view],
  );
  const totalSum = useMemo(() => {
    return (
      computeTotalSumByTradeFlow({
        data: inputData,
        tradeFlow,
        tradeDirection: dataTradeDirection,
      }) || 0
    );
  }, [inputData, tradeFlow, dataTradeDirection]);

  const mapData = useMemo(() => {
    if (
      view !== UIView.Markets ||
      locationLoading ||
      locationError ||
      !locationMetadata ||
      !worldGeojson
    ) {
      return {
        type: "FeatureCollection",
        features: [],
      } as FeatureCollection<Geometry, CountryProperties>;
    }

    const { regions, subregions, countries } = locationMetadata as {
      regions: Array<Record<string, unknown>>;
      subregions: Array<Record<string, unknown>>;
      countries: Array<Record<string, unknown>>;
    };

    return transformLocations({
      geojson: worldGeojson as FeatureCollection<Geometry, CountryProperties>,
      tradeDirection: tradeDirection,
      tradeFlow,
      data: inputData as TradeDataItem[],
      location: referenceLocation,
      regions,
      subregions,
      countries,
      isWorldToWorldQuery,
    } as Parameters<typeof transformLocations>[0]) as FeatureCollection<
      Geometry,
      CountryProperties
    >;
  }, [
    view,
    locationLoading,
    locationError,
    locationMetadata,
    worldGeojson,
    tradeDirection,
    tradeFlow,
    inputData,
    referenceLocation,
    isWorldToWorldQuery,
  ]);

  useMemo(() => {
    if (!totalSum || !mapData?.features?.length) return;

    dataRef.current = mapData.features
      .filter((d) => {
        const props = d.properties || {};
        return props.exportValue || props.importValue;
      })
      .map((d) => {
        const props = d.properties || {};
        const value =
          (props[
            tradeDirection === TradeDirection.Exports
              ? "exportValue"
              : "importValue"
          ] as number) || 0;

        return {
          Name: props.name || "",
          Year: year,
          // Use original tradeDirection for column labels (user-facing)
          [tradeDirection === TradeDirection.Exports ? "Exports" : "Imports"]:
            value,
          Share: (value / totalSum) * 100,
        };
      });
  }, [dataRef, mapData, tradeDirection, year, totalSum]);

  const countryLayer = useMemo(
    () => ({
      id: "countries",
      type: "fill" as const,
      paint: {
        "fill-color": [
          "case",
          [
            "has",
            tradeDirection === TradeDirection.Exports
              ? "exportsColor"
              : "importsColor",
          ],
          [
            "get",
            tradeDirection === TradeDirection.Exports
              ? "exportsColor"
              : "importsColor",
          ],
          "rgba(0, 0, 0, 0)",
        ] as unknown as string,
        "fill-outline-color": [
          "case",
          ["has", "strokeColor"],
          ["get", "strokeColor"],
          "rgba(0, 0, 0, 0.1)",
        ] as unknown as string,
      },
    }),
    [tradeDirection],
  );

  const hoverLayer = useMemo(
    () => ({
      id: "hoveredCountry",
      type: "line" as const,
      paint: {
        "line-color": "#ffffff",
        "line-width": 1,
      },
      filter: [
        "==",
        ["get", "countryId"],
        hoveredPolygonId,
      ] as unknown as string[],
    }),
    [hoveredPolygonId],
  );

  const onMouseMove = useCallback(
    (e: MapMouseEvent) => {
      if (!mapRef.current) return;

      const features = mapRef.current.queryRenderedFeatures(e.point, {
        layers: ["countries"],
      });

      if (features.length > 0) {
        const feature = features[0] as CountryFeature;
        const countryId = feature.properties?.countryId || "";

        // Suppress hover tooltip on the selected country when it already has a pin,
        // unless the user dismissed the pin — in which case hover should work normally.
        if (
          autoPinnedFeatureRef.current &&
          !pinnedTooltipDismissedRef?.current &&
          feature.properties?.strokeColor === primaryCountryStrokeColor
        ) {
          handleTooltip(undefined);
          setHoveredPolygonId("");
        } else {
          handleTooltip(feature);
          setHoveredPolygonId(countryId);
        }
      } else {
        handleTooltip(undefined);
        setHoveredPolygonId("");
      }
    },
    [handleTooltip],
  );

  const onMouseLeave = useCallback(() => {
    // With a separate pinned-tooltip state, only the hover tooltip needs clearing here.
    // The pinned tooltip is managed independently and persists across mouse-leave.
    handleTooltip(undefined);
    setHoveredPolygonId("");
  }, [handleTooltip]);

  /** Reproject the stored geographic centroid to current screen coords and call handlePinnedTooltip.
   *  Hides the tooltip when the country is outside the visible canvas area. */
  const repinTooltip = useCallback(() => {
    if (pinnedTooltipDismissedRef?.current) return;
    const feature = autoPinnedFeatureRef.current;
    const geoCentroid = autoPinnedGeoCentroidRef.current;
    if (!feature || !geoCentroid || !mapRef.current || !handlePinnedTooltip)
      return;
    const point = mapRef.current.project(geoCentroid);
    const canvasRect = mapRef.current.getCanvas().getBoundingClientRect();

    // Hide the tooltip when the country has been panned/zoomed outside the canvas.
    if (
      point.x < 0 ||
      point.x > canvasRect.width ||
      point.y < 0 ||
      point.y > canvasRect.height
    ) {
      handlePinnedTooltip(undefined); // hide pin only — hover manages itself
      return;
    }

    // The tooltip renders above the anchor by default (offset + height above).
    // Clamping the top ensures it never flips into below-anchor mode, so the
    // bottom only needs a small breathing-room offset.
    const tooltipOffset = dataVizLayout.tooltipOffset;
    const topInset = 2 * tooltipOffset + TOOLTIP_HEIGHT_ESTIMATE / 2;
    const bottomInset = tooltipOffset;

    const clampedX = Math.max(0, Math.min(point.x, canvasRect.width));
    const clampedY = Math.max(
      topInset,
      Math.min(point.y, canvasRect.height - bottomInset),
    );

    const screenCoords: [number, number] = [
      canvasRect.left + clampedX,
      canvasRect.top + clampedY,
    ];
    handlePinnedTooltip(feature, screenCoords);
  }, [handlePinnedTooltip, pinnedTooltipDismissedRef]);

  // Auto-pin the tooltip for the selected country when it is too small to
  // interact with reliably (bounding-box area < SMALL_COUNTRY_AREA_THRESHOLD).
  useEffect(() => {
    if (!isMapLoaded || !mapData.features.length) {
      clearAutoPinnedTooltip();
      return;
    }

    // The selected / reference country is the one tagged with the primary stroke colour.
    // When a region/subregion is selected, *all* member countries get this stroke colour,
    // so only auto-pin when exactly one feature matches (i.e. a single country is selected).
    const primaryStrokeFeatures = mapData.features.filter(
      (f) => f.properties?.strokeColor === primaryCountryStrokeColor,
    ) as CountryFeature[];
    const referenceFeature =
      primaryStrokeFeatures.length === 1 ? primaryStrokeFeatures[0] : undefined;

    if (
      referenceFeature &&
      getBoundingBoxArea(referenceFeature) < SMALL_COUNTRY_AREA_THRESHOLD
    ) {
      if (handlePinnedTooltip && mapRef.current) {
        const pinnedAnchor = getPinnedTooltipAnchorCoords(referenceFeature);
        if (!pinnedAnchor) {
          clearAutoPinnedTooltip();
          handleTooltip(referenceFeature);
        } else {
          autoPinnedFeatureRef.current = referenceFeature;
          autoPinnedGeoCentroidRef.current = pinnedAnchor;
          repinTooltip();
        }
      } else {
        clearAutoPinnedTooltip();
        handleTooltip(referenceFeature);
      }
    } else {
      clearAutoPinnedTooltip();
    }
  }, [
    mapData,
    isMapLoaded,
    handleTooltip,
    handlePinnedTooltip,
    clearAutoPinnedTooltip,
    repinTooltip,
  ]);

  const onMapLoad = useCallback(() => {
    if (mapRef.current) {
      canvasRef.current = mapRef.current.getCanvas();
    }
    setIsMapLoaded(true);
  }, [canvasRef]);

  const handleZoomIn = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  }, []);

  const handleResetZoom = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.setZoom(0);
    }
  }, []);

  return (
    <MapContainer>
      <ZoomControls
        zoom={{
          scale: ({ scaleX }) => {
            if (scaleX > 1) {
              handleZoomIn();
            } else {
              handleZoomOut();
            }
          },
          reset: handleResetZoom,
        }}
      />
      <ReactMapGL
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{
          longitude: 0,
          latitude: 20,
          zoom: 0,
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/harvardgrowthlab/ckbihvcbv1jg51hmulv16nf5r"
        renderWorldCopies={false}
        maxBounds={[
          [-180, -85], // Southwest coordinates
          [180, 85], // Northeast coordinates
        ]}
        dragRotate={false}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        onClick={onMouseMove}
        onMove={repinTooltip}
        interactiveLayerIds={["countries"]}
        preserveDrawingBuffer
        onLoad={onMapLoad}
      >
        {mapData.features.length > 0 && (
          <Source id="countries" type="geojson" data={mapData}>
            <Layer {...countryLayer} />
            <Layer {...hoverLayer} />
          </Source>
        )}
      </ReactMapGL>
      <SelectedLocationContainer data-download-exclude>
        Selected Location
      </SelectedLocationContainer>
    </MapContainer>
  );
};

const arePropsEqual = (
  prevProps: ChartProps,
  nextProps: ChartProps,
): boolean => {
  return (
    prevProps.inputData === nextProps.inputData &&
    prevProps.queryVariables.year === nextProps.queryVariables.year &&
    prevProps.queryVariables.exporter === nextProps.queryVariables.exporter &&
    prevProps.queryVariables.importer === nextProps.queryVariables.importer &&
    prevProps.queryVariables.productId === nextProps.queryVariables.productId &&
    prevProps.queryVariables.view === nextProps.queryVariables.view &&
    prevProps.tradeDirection === nextProps.tradeDirection &&
    prevProps.tradeFlow === nextProps.tradeFlow
  );
};

export default memo(Chart, arePropsEqual);
